import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import RoleRoute from './components/RoleRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import InviteManagement from './pages/InviteManagement';
import ApiDocs from './pages/ApiDocs';
import NotFound from './pages/NotFound';
import TextProcessor from './pages/tools/TextProcessor';
import CloudDisk from './pages/CloudDisk';
import CloudDiskShare from './pages/CloudDiskShare';
import Toolbox from './pages/Toolbox';
import ToastManager from './components/ToastManager';
import { disableRouterLogs } from './utils/disableRouterLogs';
import './App.css';
import './components/GlassCard.css';

const WechatAuthManagement = React.lazy(() => import('./pages/WechatAuthManagement'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const CardManagement = React.lazy(() => import('./pages/CardManagement'));
const DictionaryManagement = React.lazy(() => import('./pages/DictionaryManagement'));
const RecordLogs = React.lazy(() => import('./pages/RecordLogs'));
const SystemConfig = React.lazy(() => import('./pages/SystemConfig'));
const KmInterfaceManage = React.lazy(() => import('./pages/KmInterfaceManage'));
const KmInterfaceList = React.lazy(() => import('./pages/KmInterfaceList'));
const IOSOPLogin = React.lazy(() => import('./pages/tools/IOSOPLogin'));
const OPGameBlock = React.lazy(() => import('./pages/tools/OPGameBlock'));
const ApiChargeList = React.lazy(() => import('./pages/tools/ApiChargeList'));
const WechatGameList = React.lazy(() => import('./pages/WechatGameList'));
const Converter = React.lazy(() => import('./pages/tools/Converter'));
const IOSOPPublicLogin = React.lazy(() => import('./pages/tools/IOSOPPublicLogin'));
const HomeContentManagement = React.lazy(() => import('./pages/HomeContentManagement'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = React.lazy(() => import('./pages/BlogDetailPage'));
const BlogEditorPage = React.lazy(() => import('./pages/BlogEditorPage'));

const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
    <Spin size="large" />
  </div>
);

const locale = (zhCN && typeof zhCN === 'object' && 'default' in zhCN)
  ? (zhCN as any).default
  : zhCN;

disableRouterLogs();

dayjs.locale('zh-cn');
dayjs.extend(customParseFormat);

const AuthorizedRoutes: React.FC = () => {
  const { allowedPaths } = useAuth();

  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/home" element={<HomePage />} />
        <Route path="/data" element={<Dashboard />} />
        <Route path="/invites" element={<InviteManagement />} />
        <Route path="/api-docs" element={<ApiDocs />} />
        {allowedPaths.has('/wechat-auth') && (
          <Route path="/wechat-auth" element={
            <RoleRoute allowedRoles={['yearly_vip', 'permanent', 'super_admin']} permissionPath="/wechat-auth">
              <WechatAuthManagement />
            </RoleRoute>
          } />
        )}
        {allowedPaths.has('/admin') && (
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />
        )}
        {allowedPaths.has('/home-content') && (
          <Route path="/home-content" element={
            <AdminRoute>
              <HomeContentManagement />
            </AdminRoute>
          } />
        )}
        {allowedPaths.has('/card-management') && (
          <>
            <Route path="/card-management" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <CardManagement />
              </RoleRoute>
            } />
            <Route path="/card-management/dictionary" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <DictionaryManagement />
              </RoleRoute>
            } />
            <Route path="/card-management/record-logs" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <RecordLogs />
              </RoleRoute>
            } />
            <Route path="/card-management/system-config" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <SystemConfig />
              </RoleRoute>
            } />
            <Route path="/card-management/interface-manage" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <KmInterfaceManage />
              </RoleRoute>
            } />
            <Route path="/card-management/interface-list" element={
              <RoleRoute allowedRoles={['super_admin']} permissionPath="/card-management">
                <KmInterfaceList />
              </RoleRoute>
            } />
          </>
        )}
        <Route path="/tools/text-processor" element={<TextProcessor />} />
        <Route path="/tools/ios-op-login" element={<IOSOPLogin />} />
        <Route path="/tools/op-game-block" element={<OPGameBlock />} />
        <Route path="/tools/api-charge-list" element={<ApiChargeList />} />
        <Route path="/tools/converter" element={<Converter />} />
        {allowedPaths.has('/wechat-game-list') && (
          <Route path="/wechat-game-list" element={
            <RoleRoute allowedRoles={['super_admin']} permissionPath="/wechat-game-list">
              <WechatGameList />
            </RoleRoute>
          } />
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ConfigProvider locale={locale}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ios-op-login" element={<Suspense fallback={<LazyFallback />}><IOSOPPublicLogin /></Suspense>} />
            <Route path="/blog" element={<Suspense fallback={<LazyFallback />}><BlogListPage /></Suspense>} />
            <Route path="/blog/:id" element={<Suspense fallback={<LazyFallback />}><BlogDetailPage /></Suspense>} />
            <Route path="/blog-editor" element={
              <Suspense fallback={<LazyFallback />}>
                <AdminRoute>
                  <BlogEditorPage />
                </AdminRoute>
              </Suspense>
            } />
            <Route path="/blog-editor/:id" element={
              <Suspense fallback={<LazyFallback />}>
                <AdminRoute>
                  <BlogEditorPage />
                </AdminRoute>
              </Suspense>
            } />
            <Route path="/cloud-disk" element={<CloudDisk />} />
            <Route path="/cloud-disk/share/:code" element={<CloudDiskShare />} />
            <Route path="/toolbox" element={<Toolbox />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AuthorizedRoutes />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
        <ToastManager />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
